import React from 'react';
import {
  Alert,
  Box,
  Container,
  CopyToClipboard,
  Header,
  Link,
  SpaceBetween,
} from '@cloudscape-design/components';
import { BulletList, Marked, checkName, preWrap } from './checkDisplay';
import { categoryTabId } from './CategoryTab';
import { dashboardIds } from './humanVerification';
import { buildReviewBlock, reviewBlockText, routingNote } from './reviewBlock';
import { humanVerifyItems, isHumanVerification, readJsonField } from './reviewRecord';

/**
 * ISFR's context for the reviewer's comments, on the Reviewer Breakdown. v1 writes
 * neither the internal comment nor the partner comment: the review is still
 * missing data it needs. The reviewer copies this context into
 * their own AI tool to draft both. It keeps what is internal apart from what is
 * safe to share with the partner, and says so in the text it copies.
 */

// The checks the agent does not automate yet are the reviewer's before anything
// goes to the partner: one line points to them, rather than listing every flag.
const VerifyFirst = ({ view, onOpenTab }) => {
  if (view.checks.status !== 'ok') return null;
  const category = view.rollup.categories.find(isHumanVerification);
  if (!category || humanVerifyItems(category, view.checks.byId).length === 0) return null;
  return (
    <div data-testid="isfr-verify-first">
      <Box color="text-body-secondary">
        {'Checks not automated yet: '}
        {onOpenTab
          ? <Link onFollow={() => onOpenTab(categoryTabId(category.key))}>{`see ${category.label}`}</Link>
          : `see ${category.label}`}
        .
      </Box>
    </div>
  );
};

// The agent marks each partner finding for its dashboard; the context leaves the mark out.
const unmarked = (finding) => finding.replace(/^⚠️\s*/u, '');

const stringList = (record, field) => {
  const read = readJsonField(record, field);
  return read.status === 'ok' && Array.isArray(read.value)
    ? read.value.filter((v) => typeof v === 'string' && v.trim() !== '')
    : [];
};

/**
 * The context as sections, each {title, text} or {title, items}: what the request
 * is and what to draft, the review result, the reasons and actions, what is left
 * to verify by hand (all internal), then what is safe to share with the partner.
 * An empty section is left out.
 */
export function commentContextSections(view, request) {
  const summary = (request && request.approval_summary) || {};
  const ids = dashboardIds(request);
  const about = [
    ids.fundRequestId,
    typeof summary.partner === 'string' && summary.partner,
    typeof summary.total_fund_requested === 'string' && summary.total_fund_requested && `requested ${summary.total_fund_requested}`,
  ].filter(Boolean).join(', ');
  const hv = view.checks.status === 'ok' ? view.rollup.categories.find(isHumanVerification) : null;
  const toVerify = hv ? humanVerifyItems(hv, view.checks.byId).map(checkName) : [];
  const sections = [
    {
      title: '',
      text: [
        `Innovation Sandbox funding review${about ? `: ${about}` : ''}.`,
        'Use this context to draft two comments: an internal comment for the review record, and a partner comment '
          + 'of at most 1,000 characters. Put only the "Safe to share with the partner" part in the partner comment, and '
          + 'never mention internal amounts, thresholds, approval levels, the review tooling or AI there.',
      ].join('\n'),
    },
    { title: 'Review result (internal only)', text: reviewBlockText(buildReviewBlock(view, request), view.verdictLabel, routingNote(view)) },
    { title: 'Reasons (internal only)', items: stringList(request, 'reviewer_reasons_json') },
    { title: 'Actions (internal only)', items: stringList(request, 'reviewer_action_items_json') },
    { title: 'Still to verify by hand (internal only)', items: toVerify },
    { title: 'Safe to share with the partner: findings', items: view.partnerFindings.map(unmarked) },
    { title: 'Safe to share with the partner: required actions', items: view.partnerActions },
  ];
  return sections.filter((s) => (s.items ? s.items.length > 0 : Boolean(s.text)));
}

/** The context as plain text to paste: each section under its title, a blank line between. */
export const commentContextText = (sections) => sections.map((s) => [
  s.title && `${s.title}:`,
  s.items ? s.items.map((i) => `- ${i}`).join('\n') : s.text,
].filter(Boolean).join('\n')).join('\n\n');

// Characters Markdown would read as formatting are escaped, so the record's own
// words (a file name with underscores, the "<...>" guidance placeholder, which
// would otherwise vanish as an HTML tag) come through as written. A link is left
// whole.
export const escapeMarkdown = (text) => text
  .split(/(https?:\/\/\S+)/)
  .map((piece, i) => (i % 2 === 1 ? piece : piece.replace(/[\\`*_[\]<>]/g, '\\$&')))
  .join('')
  .replace(/^([#+-])/, '\\$1')
  .replace(/^(\d+)([.)])/, '$1\\$2');

/** The context as Markdown: each title bold, lists as bullets, every line escaped. */
export const commentContextMarkdown = (sections) => sections.map((s) => [
  s.title && `**${escapeMarkdown(s.title)}:**`,
  s.items
    ? s.items.map((i) => `- ${escapeMarkdown(i.replace(/\n+/g, ' '))}`).join('\n')
    : s.text.split('\n').map((line) => (line.trim() ? escapeMarkdown(line) : '')).join('  \n'),
].filter(Boolean).join('\n')).join('\n\n');

// How long a copy is. A word holds a letter or digit, so a "- " bullet or a "**"
// marker is not counted. Characters count every one copied, spaces and line breaks
// included, by code point.
export const copyLength = (text) => {
  const words = text.split(/\s+/).filter((token) => /[\p{L}\p{N}]/u.test(token)).length;
  const characters = Array.from(text).length;
  const plural = (n, unit) => `${n.toLocaleString('en-US')} ${unit}${n === 1 ? '' : 's'}`;
  return `${plural(words, 'word')} · ${plural(characters, 'character')}`;
};

const CopyWithLength = ({ label, success, failure, text }) => (
  <div data-testid="isfr-context-copy">
    <SpaceBetween direction="horizontal" size="xs" alignItems="center">
      <CopyToClipboard variant="button" copyButtonText={label} copySuccessText={success} copyErrorText={failure} textToCopy={text} />
      <Box variant="small" color="text-body-secondary">{copyLength(text)}</Box>
    </SpaceBetween>
  </div>
);

export const CommentContext = ({ request, view, onOpenTab }) => {
  const sections = commentContextSections(view, request);
  const text = commentContextText(sections);
  return (
    <Container header={<Header variant="h2">Context for your comments</Header>}>
      <SpaceBetween size="m">
        <VerifyFirst view={view} onOpenTab={onOpenTab} />
        <div data-testid="isfr-context-notice">
          <Alert type="info" header="No automated comments yet">
            This version does not write the internal comment or the partner comment, because the review is still
            missing some of the data it needs. Copy the context below into your own AI tool to draft both, and check
            what it writes before you use it. A future version will write them once that data access is in place.
          </Alert>
        </div>
        <SpaceBetween direction="horizontal" size="l" alignItems="center">
          <CopyWithLength label="Copy as Markdown" success="Markdown copied" failure="Markdown failed to copy" text={commentContextMarkdown(sections)} />
          <CopyWithLength label="Copy as text" success="Text copied" failure="Text failed to copy" text={text} />
        </SpaceBetween>
        <div data-testid="isfr-comment-context" style={{ border: '1px solid #d5dbdb', borderRadius: '8px', padding: '12px' }}>
          <SpaceBetween size="s">
            {sections.map((s, i) => (
              <div key={i}>
                {s.title && <Box variant="strong">{`${s.title}:`}</Box>}
                {s.items
                  ? <BulletList items={s.items} />
                  : <Box><span style={preWrap}><Marked text={s.text} /></span></Box>}
              </div>
            ))}
          </SpaceBetween>
        </div>
      </SpaceBetween>
    </Container>
  );
};
